import { useEffect, useState } from 'preact/hooks'
import { landingPagesApi } from '@/api/landingPages'
import type { FormFieldCatalogResponseDto } from '@/types/landingPage'
import { useWorkspaceInboundWebhooks } from '@/hooks/useInboundWebhooks'
import type { QuizDto } from '@/api/webs'
import { quizBlockExample, quizPlacementSentence, notApplicableSentence } from './inboundWebhookQuiz'

interface InboundWebhookSnippetProps {
    /** The web's id. Present only at web level, the one complete example. */
    webId?: string | null
    /** Set with clientCompanyId and workspaceId to check whether a usable key exists here. */
    showKeyAvailability?: boolean
    clientCompanyId?: string | null
    workspaceId?: string | null
    /** The web's live quiz and its version (ORB-915). Only meaningful with webId: the example then uses its question IDs. */
    signedOffQuiz?: { quiz: QuizDto; version: number } | null
}

const WEB_ID_PLACEHOLDER = '<WEB_ID>'
const KEY_PLACEHOLDER = '<YOUR_KEY>'

const preClass = 'p-2 rounded bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto whitespace-pre'
const codeClass = 'px-1 rounded bg-slate-100 dark:bg-slate-700'

/**
 * The copy-paste example for posting a lead into OrbitalX.
 *
 * A submission carries exactly two things that address it: the key in a header, and web_id in
 * the body. It also has to carry an email, which is what a lead is built from.
 * The key names both the company that owns it and, through the subscription it matches, the
 * workspace the lead lands in — so nothing in the URL points anywhere, and there is no field a
 * caller could set that has to agree with the key.
 *
 * web_id stays in the body rather than the query string. Both are inside TLS, but query
 * strings are captured by request telemetry, platform logs and proxies. Only web_id varies by
 * where this renders, and the web settings tab is the one place it is in scope.
 */
export function InboundWebhookSnippet({
    webId,
    showKeyAvailability = false,
    clientCompanyId,
    workspaceId,
    signedOffQuiz,
}: InboundWebhookSnippetProps) {
    const apiBase = ((import.meta as ImportMeta).env?.VITE_API_BASE_URL || '').replace(/\/$/, '')
    const endpoint = `${apiBase}/api/FormSubmissions/Webhook`
    const resolvedWebId = webId || WEB_ID_PLACEHOLDER

    // web_id is first in the body because it is the field integrators forget, and its absence
    // is the single most common reason a submission is rejected.
    const curlExample = `curl -X POST '${endpoint}' \\
  -H 'Authorization: Bearer ${KEY_PLACEHOLDER}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "web_id": "${resolvedWebId}",
    "email": "lead@example.com",
    "firstName": "Jane",
    "lastName": "Doe",
    "company": "Acme Inc.",
${quizBlockExample(webId ? signedOffQuiz?.quiz ?? null : 'placeholder')}${customFieldsExample(!!webId)}
  }'`

    const copy = (text: string) => { navigator.clipboard?.writeText(text) }

    return (
        <div className="space-y-3 text-xs text-slate-600 dark:text-slate-300">
            <p className="leading-relaxed">
                Post leads into OrbitalX from any external system &mdash; Zapier, an AI SDR service, a legacy CRM, a custom script, or a
                one-off backfill. Submissions land in the same ingest pipeline as landing-page leads, so validation, rate limiting and
                downstream enrichment all apply automatically.
            </p>

            {showKeyAvailability && workspaceId && (
                <KeyAvailability clientCompanyId={clientCompanyId ?? null} workspaceId={workspaceId} />
            )}

            <div className="space-y-1">
                <p className="font-medium text-slate-700 dark:text-slate-200">What addresses a submission</p>
                <ul className="space-y-1 list-disc pl-4 leading-relaxed">
                    <li>
                        <code className={codeClass}>Authorization: Bearer</code> &mdash; the key, shown once when it was created. It names the
                        workspace leads land in, so there is nothing in the URL and nothing else to configure. Never put it in a query
                        string, a body field, or a log line.
                    </li>
                    <li>
                        <code className={codeClass}>web_id</code> in the body &mdash; which web the lead converted on. It is the web&apos;s
                        id: the part after <code className={codeClass}>/web/</code> in its address in the dashboard.
                        {webId
                            ? ' Filled in above for this web.'
                            : ' Replace the placeholder with the id of the web you want leads to land on.'}
                        {' '}A submission without a valid one is rejected with a <code className={codeClass}>400</code>; it is never accepted
                        and quietly dropped.
                    </li>
                </ul>
            </div>

            <div>
                <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">
                        curl example
                        {webId && (
                            <span className="ml-1 font-normal text-slate-500 dark:text-slate-400">
                                &mdash; ready to run, once you paste your key
                            </span>
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={() => copy(curlExample)}
                        className="text-[10px] text-primary-600 dark:text-primary-400 hover:underline"
                    >
                        Copy
                    </button>
                </div>
                <pre className={preClass}>{curlExample}</pre>
            </div>

            <AcceptedFields />

            {webId
                ? signedOffQuiz && <WebQuizAnswers quiz={signedOffQuiz.quiz} version={signedOffQuiz.version} />
                : <QuizAnswersFormat />}

            <div className="space-y-2">
                <p className="font-medium text-slate-700 dark:text-slate-200">Custom fields</p>
                <p className="leading-relaxed">
                    <code className={codeClass}>customFields</code> is a list of <code className={codeClass}>{'{ id, label, value }'}</code>. OrbitalX
                    stores each one as sent and shows it on the lead under Custom fields. Nothing checks them, so the label you send is the
                    label people see.
                </p>
            </div>
        </div>
    )
}

// One example field anywhere; a web's own example shows two so the list shape is obvious.
function customFieldsExample(forWeb: boolean): string {
    const fields = [
        '{ "id": "organisation_type", "label": "Organisation type", "value": "Central government department" }',
        ...(forWeb ? ['{ "id": "portfolio_size", "label": "Portfolio size", "value": "25 to 50 programmes" }'] : []),
    ]
    return `    "customFields": [\n${fields.map(field => `      ${field}`).join(',\n')}\n    ]`
}

function QuizRow({ name, description, note }: { name: string; description: string; note: string }) {
    return (
        <tr className="border-t border-slate-200 dark:border-slate-700">
            <td className="py-1 pr-3 align-top whitespace-nowrap"><code className={codeClass}>{name}</code></td>
            <td className="py-1 pr-3 align-top text-slate-500 dark:text-slate-400">{description}</td>
            <td className="py-1 align-top text-slate-400 dark:text-slate-500 whitespace-nowrap">{note}</td>
        </tr>
    )
}

const tableCaptionClass = 'text-[10px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500 text-left pb-1'

/** The quiz block in general, where no web is in scope (Integrations, workspace settings). */
function QuizAnswersFormat() {
    return (
        <div className="space-y-2">
            <p className="font-medium text-slate-700 dark:text-slate-200">Quiz answers</p>
            <p className="leading-relaxed">
                On a Download web with a quiz, send the person&apos;s answers in a <code className={codeClass}>quiz</code> block; leave it out on
                any other web. Pages OrbitalX hosts already do this; you need it only for a quiz built outside OrbitalX. That web&apos;s Settings
                tab shows the example with its own question IDs.
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <caption className={tableCaptionClass}>Quiz</caption>
                    <tbody>
                        <QuizRow name="quiz.answers" description="One entry for each question on the web’s quiz, in any order. Omit the property when starting with details only" note="Required to complete" />
                        <QuizRow name="answers[].id" description="The question’s ID, from the web’s Quiz tab" note="Required" />
                        <QuizRow name="answers[].value" description="A whole number on the quiz’s scale, or null for Not applicable" note="Required" />
                        <QuizRow name="answers[].label" description="The question text, for your own records; OrbitalX keeps its own wording" note="Optional" />
                        <QuizRow name="quiz.attemptId" description="A UUID for this attempt. Reuse it on details, answers and retries; use a new ID for a retake" note="Required for two posts; optional for one" />
                        <QuizRow name="quiz.score, quiz.band" description="Not read; OrbitalX works out the score and level from the answers" note="Ignored" />
                    </tbody>
                </table>
            </div>
            <p className="leading-relaxed">
                Answers are checked against the web&apos;s signed-off quiz; a block that doesn&apos;t match it is refused with a{' '}
                <code className={codeClass}>400</code> that says why. If the quiz asks for details before the questions, post the details first,
                then post the same fields again with the <code className={codeClass}>quiz</code> block added. Either post can be processed
                first, so both need every detail. The two become one submission, and nothing is sent until the answers arrive. An answers post whose name doesn&apos;t match is kept as its
                own submission.
            </p>
        </div>
    )
}

/** This web's live questions, so an integrator building their own quiz page has the exact IDs. */
function WebQuizAnswers({ quiz, version }: { quiz: QuizDto; version: number }) {
    const allowsNotApplicable = quiz.Questions.filter(question => question.AllowsNotApplicable).map(question => question.Id)
    return (
        <div className="space-y-2">
            <p className="font-medium text-slate-700 dark:text-slate-200">Quiz answers on this web</p>
            <p className="leading-relaxed">
                From the signed-off quiz, version {version}. Send one entry for each question as{' '}
                <code className={codeClass}>{'{ id, value }'}</code>. Pages OrbitalX hosts already do this; you need it only for a quiz built
                outside OrbitalX.
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <caption className={tableCaptionClass}>Questions</caption>
                    <tbody>
                        {quiz.Questions.map(question => (
                            <QuizRow
                                key={question.Id}
                                name={question.Id}
                                description={question.Text ?? ''}
                                note={`1 to ${quiz.ScaleMax}${question.AllowsNotApplicable ? ', or null' : ''}`}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="leading-relaxed">
                {notApplicableSentence(allowsNotApplicable).map((part, index) =>
                    part.code ? <code key={index} className={codeClass}>{part.text}</code> : <span key={index}>{part.text}</span>)}
                {' '}A posted score or band is not read; OrbitalX works them out. Answers that don&apos;t match this quiz are refused with a{' '}
                <code className={codeClass}>400</code> that says why. {quizPlacementSentence(quiz.DetailsPlacement)} To retry the same answers
                safely, send a UUID you make for each attempt as <code className={codeClass}>quiz.attemptId</code> and send the same one on the
                retry, so it isn&apos;t stored as another attempt.
            </p>
        </div>
    )
}

/**
 * Which fields the endpoint will actually read.
 *
 * The curl example shows five, the endpoint takes far more, and nothing said so — the only way to
 * learn that UTM parameters were accepted was to be told. Read from the same catalog the landing-page
 * field picker uses, so this cannot drift from what the ingest normalizer recognises.
 */
function AcceptedFields() {
    const [catalog, setCatalog] = useState<FormFieldCatalogResponseDto | null>(null)

    useEffect(() => {
        let cancelled = false
        // Caught rather than left to float: this is a reference table beside a working example, so a
        // failed lookup should leave the example alone, not surface as an unhandled rejection.
        void landingPagesApi.getFormFieldCatalog()
            .then(response => { if (!cancelled && response.data) setCatalog(response.data) })
            .catch(() => { /* rendering nothing is the correct answer here */ })
        return () => { cancelled = true }
    }, [])

    // Says nothing rather than guessing: the example above is correct with or without this.
    if (!catalog) return null

    return (
        <div className="space-y-2">
            <p className="font-medium text-slate-700 dark:text-slate-200">Fields the endpoint accepts</p>
            <p className="leading-relaxed">
                Send any of these alongside <code className={codeClass}>web_id</code>. Common spellings are
                recognised too &mdash; <code className={codeClass}>company_name</code>,{' '}
                <code className={codeClass}>Job Title</code> and <code className={codeClass}>jobtitle</code> all reach the
                same field &mdash; and anything unrecognised is kept on the raw submission rather than rejected.
            </p>
            <p className="leading-relaxed">
                <code className={codeClass}>email</code> is the only one this endpoint requires; a submission without one
                is rejected with a <code className={codeClass}>400</code>, the same as a missing{' '}
                <code className={codeClass}>web_id</code>. Names are optional here even though a landing-page form
                asks for them.
            </p>

            <FieldTable caption="Lead details" fields={catalog.FormFields} />
            <FieldTable caption="Campaign tracking" fields={catalog.TrackingFields} />

            <p className="leading-relaxed">
                <code className={codeClass}>overridden_submission_date</code> is accepted as well. Set it to an ISO 8601
                timestamp when backfilling historical leads, so they carry the date they actually engaged rather than the
                date you loaded them.
            </p>
        </div>
    )
}

/**
 * Three states, not two. An API deployed before this field existed omits it, and rendering that as
 * Optional would put email — the one field the route rejects a submission for — in the Optional
 * column, which is the exact lie this table was changed to stop telling. Say nothing instead; the
 * paragraph above still states the rule in words.
 */
function requiredness(isRequiredForWebhook: boolean | undefined) {
    if (isRequiredForWebhook === undefined) return '\u2014'
    return isRequiredForWebhook ? 'Required' : 'Optional'
}

function FieldTable({ caption, fields }: { caption: string; fields: FormFieldCatalogResponseDto['FormFields'] }) {
    if (fields.length === 0) return null

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <caption className="text-[10px] font-semibold tracking-wider uppercase text-slate-400 dark:text-slate-500 text-left pb-1">
                    {caption}
                </caption>
                <tbody>
                    {fields.map(field => (
                        <tr key={field.CanonicalKey} className="border-t border-slate-200 dark:border-slate-700">
                            <td className="py-1 pr-3 align-top">
                                <code className={codeClass}>{field.JsonKey}</code>
                            </td>
                            <td className="py-1 pr-3 align-top text-slate-500 dark:text-slate-400">
                                {field.DisplayLabel}
                            </td>
                            <td className="py-1 align-top text-slate-400 dark:text-slate-500 whitespace-nowrap">
                                {requiredness(field.IsRequiredForWebhook)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

/**
 * Says whether a key that works here actually exists. Without it the example reads as ready to use
 * even when nobody has created one, and the first thing the integrator sees is a 401.
 */
function KeyAvailability({ clientCompanyId, workspaceId }: { clientCompanyId: string | null; workspaceId: string }) {
    const { webhooks, loading, error } = useWorkspaceInboundWebhooks(clientCompanyId, workspaceId)

    // A failed or pending lookup says nothing rather than guessing; the example below is still correct.
    if (loading || error || webhooks === null) return null

    if (webhooks.length === 0) {
        return (
            <div className="p-2 rounded border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300 leading-relaxed">
                No inbound webhook key can post into this workspace yet. A company administrator can create one under
                Settings &rarr; Integrations &rarr; Inbound webhooks; the key is shown once at that point.
            </div>
        )
    }

    return (
        <div className="p-2 rounded border border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/50 leading-relaxed">
            <span className="font-medium text-slate-700 dark:text-slate-200">
                {webhooks.length === 1 ? 'Key available: ' : 'Keys available: '}
            </span>
            {webhooks.map((webhook, index) => (
                <span key={webhook.Id}>
                    {index > 0 && ', '}
                    {webhook.Name}
                    {!webhook.LastReceivedUtc && (
                        <span className="text-slate-500 dark:text-slate-400"> (never used)</span>
                    )}
                </span>
            ))}
        </div>
    )
}
